import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { Users, Plus, Radio, Crown, Check, Lock, Copy, ArrowRight, MessageSquare, Trophy } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";

const STATUS_STYLE = {
  open: { label: "Open", color: "#00FF66" },
  locked: { label: "Live", color: "#FF9F0A" },
  settled: { label: "Final", color: "#71717A" },
  cancelled: { label: "Cancelled", color: "#FF3B30" },
};

function PlanPicker({ catalog, onDone }) {
  const [plan, setPlan] = useState("friends_3");
  const [sports, setSports] = useState(["NFL"]);
  const [busy, setBusy] = useState(false);

  const limit = catalog.plans.find((p) => p.id === plan)?.sport_limit || 1;

  const toggleSport = (id, live) => {
    if (!live) return;
    setSports((prev) => {
      if (prev.includes(id)) return prev.filter((s) => s !== id);
      if (prev.length >= limit) return [...prev.slice(1), id];
      return [...prev, id];
    });
  };

  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/friends/plan/checkout", {
        plan, sports, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <div className="border border-[#7C3AED]/40 bg-[#7C3AED]/[0.06] p-6" data-testid="friends-plan-picker">
      <div className="text-[#C4B5FD] text-xs uppercase tracking-widest mb-1">Membership</div>
      <h2 className="font-display text-3xl mb-1">PICK YOUR PLAN</h2>
      <p className="text-white/60 text-sm mb-5">
        Your subscription is the only charge on PickemSports — challenges themselves are always free
        to enter, with no cash prizes.
      </p>

      <div className="grid md:grid-cols-3 gap-2 mb-5">
        {catalog.plans.map((p) => (
          <button
            key={p.id}
            onClick={() => { setPlan(p.id); setSports((s) => s.slice(0, p.sport_limit)); }}
            data-testid={`plan-option-${p.id}`}
            className={`text-left p-4 border rounded-sm transition-colors ${
              plan === p.id
                ? "border-[#7C3AED] bg-[#7C3AED]/15"
                : "border-white/10 hover:border-white/30 bg-[#0a0a0a]"
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-widest text-white/50">{p.label}</span>
              {plan === p.id && <Check size={14} className="text-[#C4B5FD]" />}
            </div>
            <div className="font-display text-3xl mt-1">
              ${p.price.toFixed(2)}
              <span className="text-white/40 text-sm">/mo</span>
            </div>
            <div className="text-white/50 text-xs mt-1">{p.blurb}</div>
          </button>
        ))}
      </div>

      <div className="text-white/40 text-[10px] uppercase tracking-widest mb-2">
        Choose your sport{limit > 1 ? `s · up to ${limit}` : ""}
      </div>
      <div className="flex flex-wrap gap-1.5 mb-5">
        {catalog.sports.map((s) => {
          const on = sports.includes(s.id);
          return (
            <button
              key={s.id}
              onClick={() => toggleSport(s.id, s.live)}
              disabled={!s.live}
              data-testid={`sport-option-${s.id}`}
              className={`px-2.5 py-1.5 text-xs uppercase tracking-widest rounded-sm border inline-flex items-center gap-1.5 ${
                !s.live
                  ? "border-white/5 text-white/25 cursor-not-allowed"
                  : on
                  ? "border-[#00FF66] text-[#00FF66] bg-[#00FF66]/10"
                  : "border-white/15 text-white/60 hover:border-white/40"
              }`}
            >
              {!s.live && <Lock size={10} />}
              {s.label}
              {!s.live && <span className="text-[9px] normal-case tracking-normal">soon</span>}
            </button>
          );
        })}
      </div>

      <button
        onClick={subscribe}
        disabled={busy || !sports.length}
        data-testid="friends-subscribe-btn"
        className="w-full py-3 bg-[#7C3AED] hover:bg-[#6D28D9] disabled:opacity-50 text-white font-bold uppercase tracking-widest text-sm rounded-sm"
      >
        {busy ? "Opening checkout..." : `Subscribe · $${catalog.plans.find((p) => p.id === plan)?.price.toFixed(2)}/mo`}
      </button>
      <div className="mt-2 text-white/30 text-[10px] text-center">
        Cancel any time · no entry fees · no cash prizes · 18+
      </div>
      {onDone && (
        <button onClick={onDone} className="mt-2 w-full text-white/40 text-xs hover:text-white/70">
          Maybe later
        </button>
      )}
    </div>
  );
}

function ChallengeRow({ c }) {
  const s = STATUS_STYLE[c.status] || STATUS_STYLE.open;
  return (
    <Link
      to={`/contest/${c.contest_id}`}
      data-testid={`challenge-row-${c.contest_id}`}
      className="block border border-white/10 bg-[#141414] hover:border-[#00FF66]/40 p-4 transition-colors"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-semibold flex items-center gap-2 flex-wrap">
            {c.title}
            <span
              className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border inline-flex items-center gap-1"
              style={{ color: s.color, borderColor: `${s.color}66` }}
            >
              {c.status === "locked" && <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: s.color }} />}
              {s.label}
            </span>
            {c.is_manager && (
              <span className="text-[10px] uppercase tracking-widest text-[#FFD700] border border-[#FFD700]/40 px-1.5 py-0.5 rounded-sm inline-flex items-center gap-1">
                <Crown size={9} /> Manager
              </span>
            )}
          </div>
          <div className="text-white/40 text-[10px] uppercase tracking-widest mt-0.5">
            {c.sport} · {c.position} · {c.members} invited · {c.field}/{c.max_entries} entered
          </div>
        </div>
        <div className="flex items-center gap-4 text-right">
          <div>
            <div className="text-white/40 text-[10px] uppercase tracking-widest">Chat</div>
            <div className="font-display text-lg inline-flex items-center gap-1">
              <MessageSquare size={12} className="text-white/40" />{c.messages}
            </div>
          </div>
          <div>
            <div className="text-white/40 text-[10px] uppercase tracking-widest">You</div>
            <div className="font-display text-lg">
              {c.entered ? (c.my_rank ? `#${c.my_rank}` : `${c.my_score} pts`) : "—"}
            </div>
          </div>
          <ArrowRight size={16} className="text-white/30" />
        </div>
      </div>
    </Link>
  );
}

export default function Friends() {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [mine, setMine] = useState(null);
  const [code, setCode] = useState("");
  const [joining, setJoining] = useState(false);

  usePageMeta({
    title: "Friends Only · PickemSports.club",
    description: "Free invite-only skills challenges with your crew. No entry fees, no cash prizes.",
    path: "/friends",
    noindex: true,
  });

  // A Game Manager's share link (/friends?m=CODE) credits their revenue share, write-once.
  useEffect(() => {
    const m = new URLSearchParams(window.location.search).get("m");
    if (m) api.post("/friends/manager/attribute", { code: m }).catch(() => {});
  }, []);

  const load = async () => {
    const [{ data: cat }, { data: challenges }] = await Promise.all([
      api.get("/friends/catalog"),
      api.get("/friends/challenges"),
    ]);
    setCatalog(cat);
    setMine(challenges);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const join = async () => {
    if (!code.trim()) return toast.error("Enter the invite code");
    setJoining(true);
    try {
      const { data } = await api.post("/friends/join", { code: code.trim() });
      toast.success(data.already_member ? "You're already in — opening it up" : "You're in!");
      navigate(`/contest/${data.contest_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not join");
    } finally { setJoining(false); }
  };

  if (!catalog || !mine) {
    return (
      <Layout>
        <div className="text-white/40 py-20 text-center font-display text-2xl tracking-widest animate-pulse">
          LOADING...
        </div>
      </Layout>
    );
  }

  const plan = catalog.my_plan;
  const all = [...mine.managing, ...mine.playing];

  return (
    <Layout>
      <div className="mb-8">
        <div className="text-[#00FF66] text-xs uppercase tracking-widest mb-2">
          Invite only · free to play · no cash prizes
        </div>
        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl tracking-tight">FRIENDS ONLY</h1>
        <p className="text-white/50 text-sm mt-2 max-w-2xl">
          Run a top-5 skills challenge for your crew. You pick the sport, the position and the
          tiebreaker, invite up to {catalog.max_field} friends, and trash talk in the challenge chat.
        </p>
      </div>

      {plan.active ? (
        <div className="border border-[#00FF66]/30 bg-[#00FF66]/[0.05] p-4 mb-6 flex items-center justify-between gap-3 flex-wrap"
          data-testid="friends-plan-active">
          <div>
            <div className="text-[#00FF66] text-[10px] uppercase tracking-widest">Membership active</div>
            <div className="font-display text-2xl">
              {plan.label} · {plan.sports.join(", ")}
            </div>
            {plan.renews_at && (
              <div className="text-white/40 text-[10px] uppercase tracking-widest">
                Renews {new Date(plan.renews_at).toLocaleDateString()}
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link
              to="/friends/new"
              data-testid="create-challenge-btn"
              className="bg-[#00FF66] hover:bg-[#00e65b] text-black font-bold uppercase tracking-widest px-4 py-2.5 text-xs rounded-sm inline-flex items-center gap-2"
            >
              <Plus size={14} /> New challenge
            </Link>
            <Link
              to="/manager"
              data-testid="manager-hub-btn"
              className="border border-white/20 hover:border-white/40 text-white font-bold uppercase tracking-widest px-4 py-2.5 text-xs rounded-sm inline-flex items-center gap-2"
            >
              <Crown size={14} /> Manager hub
            </Link>
          </div>
        </div>
      ) : (
        <div className="mb-6">
          <PlanPicker catalog={catalog} />
        </div>
      )}

      <div className="border border-white/10 bg-[#141414] p-4 mb-6" data-testid="join-by-code">
        <div className="text-white/40 text-xs uppercase tracking-widest mb-2">Got an invite code?</div>
        <div className="flex gap-2 flex-wrap">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="e.g. 63BELD"
            maxLength={8}
            data-testid="join-code-input"
            className="flex-1 min-w-[160px] bg-[#0a0a0a] border border-white/10 focus:border-[#00FF66] rounded-sm px-3 py-2.5 outline-none font-display text-xl tracking-[0.2em]"
          />
          <button
            onClick={join}
            disabled={joining}
            data-testid="join-code-btn"
            className="bg-white/10 hover:bg-white/20 text-white font-bold uppercase tracking-widest px-5 py-2.5 text-xs rounded-sm inline-flex items-center gap-2"
          >
            <Users size={14} /> {joining ? "Joining..." : "Join"}
          </button>
        </div>
      </div>

      {all.length === 0 ? (
        <div className="border border-white/10 bg-[#141414] p-12 text-center" data-testid="friends-empty">
          <Trophy size={40} className="text-white/20 mx-auto mb-4" />
          <div className="font-display text-2xl mb-2">NO CHALLENGES YET</div>
          <p className="text-white/50 text-sm">
            {plan.active
              ? "Create one and send the code to your group chat."
              : "Subscribe to run a challenge, or join one with a friend's code."}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {mine.managing.length > 0 && (
            <div>
              <div className="text-white/40 text-xs uppercase tracking-widest mb-2 inline-flex items-center gap-1.5">
                <Crown size={12} /> Running ({mine.managing.length})
              </div>
              <div className="space-y-2" data-testid="managing-list">
                {mine.managing.map((c) => <ChallengeRow key={c.contest_id} c={c} />)}
              </div>
            </div>
          )}
          {mine.playing.length > 0 && (
            <div>
              <div className="text-white/40 text-xs uppercase tracking-widest mb-2 inline-flex items-center gap-1.5">
                <Radio size={12} /> Playing ({mine.playing.length})
              </div>
              <div className="space-y-2" data-testid="playing-list">
                {mine.playing.map((c) => <ChallengeRow key={c.contest_id} c={c} />)}
              </div>
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}

export { PlanPicker };
